import {
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useRef } from 'react';
import type { CanonicalFieldDef } from '../../domain/canonicalFieldDef';

type Props = {
  title: string;
  fields: CanonicalFieldDef[];
  onChange: (fields: CanonicalFieldDef[]) => void;
};

let nextRowId = 0;
function createRowId(): string {
  nextRowId += 1;
  return `canonical-field-row-${nextRowId}`;
}

export function CanonicalFieldTable({ title, fields, onChange }: Props) {
  const rowIdsRef = useRef<string[]>([]);

  while (rowIdsRef.current.length < fields.length) {
    rowIdsRef.current.push(createRowId());
  }
  if (rowIdsRef.current.length > fields.length) {
    rowIdsRef.current.splice(fields.length);
  }

  const update = (index: number, patch: Partial<CanonicalFieldDef>) => {
    const next = fields.map((f, i) => (i === index ? { ...f, ...patch } : f));
    onChange(next);
  };

  const removeRow = (index: number) => {
    rowIdsRef.current.splice(index, 1);
    onChange(fields.filter((_, idx) => idx !== index));
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">{title}</Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() =>
            onChange([...fields, { path: '', description: '', required: true }])
          }
        >
          Add field
        </Button>
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Path</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Required</TableCell>
            <TableCell width={48} />
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((f, i) => (
            <TableRow key={rowIdsRef.current[i]}>
              <TableCell>
                <TextField
                  size="small"
                  value={f.path}
                  onChange={e => update(i, { path: e.target.value })}
                  placeholder="credentials.username"
                  fullWidth
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  value={f.description ?? ''}
                  onChange={e => update(i, { description: e.target.value })}
                  fullWidth
                />
              </TableCell>
              <TableCell>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={f.required}
                      onChange={e => update(i, { required: e.target.checked })}
                    />
                  }
                  label="Required"
                />
              </TableCell>
              <TableCell>
                <IconButton
                  size="small"
                  onClick={() => removeRow(i)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {fields.length === 0 && (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography variant="caption" color="text.secondary">
                  No fields defined. Add paths vendors should map in Provider Onboarding.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Stack>
  );
}
